import { useIntl } from 'react-intl';

import {
  getSchemaKind,
  type DocumentTemplateSummary,
  type EncounterKind,
  type EncounterSchema,
} from '@/entities/templates';
import type { TranscriptorConsultation } from '@/entities/transcriptor';

import { ENCOUNTER_STATUS_TONES, i18n } from '../config';
import { getRecordedSeconds } from '../lib';
import type { EncounterListItem } from '../ui/EncounterList';

export const getEncounterTitle = (consultation: TranscriptorConsultation) =>
  consultation.name ||
  consultation.documents.find((item) => item.type === 'protocol')?.name ||
  consultation.schemaName ||
  '';

// How an encounter is named and labelled in the workspace lists and headers.
export const useEncounterPresentation = ({
  schemas,
  documentTemplates,
}: {
  schemas: EncounterSchema[];
  documentTemplates: DocumentTemplateSummary[];
}) => {
  const { formatMessage } = useIntl();

  const kindOf = (consultation: TranscriptorConsultation): EncounterKind => {
    const schema = schemas.find((item) => item.id === consultation.schemaId);
    if (schema) {
      return getSchemaKind(schema, documentTemplates);
    }
    return consultation.documents.some((item) => item.type === 'protocol')
      ? 'treatment'
      : 'consultation';
  };
  const kindLabel = (kind: EncounterKind) =>
    formatMessage(i18n.encounterKindMessages[kind]);
  const statusLabel = (consultation: TranscriptorConsultation) =>
    formatMessage(i18n.encounterStatusMessages[consultation.status]);

  const toListItem = (
    consultation: TranscriptorConsultation,
  ): EncounterListItem => {
    const kind = kindOf(consultation);
    return {
      id: consultation.id,
      date: consultation.date,
      kind,
      kindLabel: kindLabel(kind),
      title: getEncounterTitle(consultation),
      status: statusLabel(consultation),
      statusTone: ENCOUNTER_STATUS_TONES[consultation.status],
      recordedSeconds: getRecordedSeconds(consultation),
    };
  };

  return { kindOf, kindLabel, statusLabel, toListItem };
};
